import * as _ from 'lodash';
import { toastr } from 'react-redux-toastr';

import { getReqControllerSignal } from 'common/utils';
import { sensorsConstants } from './sensors.constants';
import { humanize } from '../../../common/helpers/humanize';
import { axiosInstance, ENDPOINT } from '../../../common/constants';
import { handleResponse } from '../../../common/helpers';
import { getHierarchy } from '../../AssetHierarchy/actions/assetDetails.actions';
import {
  StandardSensors,
  VectorSensors,
  vibrationMeasurementTypeEnum,
  vibrationMeasurementBlocks
} from '../constants/sensors.constants';
import { toUIFormat } from '../utils/eventConfigTransform';

const organisationSitesInFlight = {};

// new APIs
export const getSensorsList = (filters, search_key, page, storeInRedux = true) => (dispatch, getState) => {
  const request = page => ({ type: sensorsConstants.GET_SENSORS_LIST_REQUEST, page });
  const success = (sensors, page, total_pages, total_count) =>
    ({ type: sensorsConstants.GET_SENSORS_LIST_SUCCESS, sensors, page, total_pages, total_count });
  const failure = error => ({ type: sensorsConstants.GET_SENSORS_LIST_FAILURE, error });

  const sensorsState = getState().sensors;
  const adminDashboardState = getState().adminDashboard;
  const params = {
    per_page: sensorsConstants.NUMBER_OF_SENSORS_PER_PAGE,
    page: page || sensorsState.items.page,
    filters: filters || sensorsState.filters.filters,
    search_key: search_key || sensorsState.filters.search_key,
    all: adminDashboardState.adminOpen
  };
  if (storeInRedux) dispatch(request(params.page));
  if (!_.isEmpty(sensorsState.sorter)) {
    params.order_by = { field: sensorsState.sorter.sorter.name, direction: sensorsState.sorter.sorter.order };
  }
  const signal = getReqControllerSignal(
    getState(),
    sensorsConstants.GET_SENSORS_LIST_REQUEST
  );
  return handleResponse(axiosInstance.get(ENDPOINT.GET_SENSORS_LIST, { params, signal })).then(
    (res) => {
      // O(n2) complexity
      let items = [];
      if (res.page === 1) items = res.items;
      else items = res.items.filter(sensor => !sensorsState.items.object.find(i => i.serial_number === sensor.serial_number));
      if (storeInRedux) dispatch(success(items, res.page, res.total_pages, res.total_count));
      return res;
    },
    (err) => {
      if (signal && signal.aborted) return;
      if (storeInRedux) dispatch(failure(err));
      throw err;
    }
  );
};

// For machine builder sensor assignment, not to mess with the actual sensor lsit.
// `includeTransmitters`: when true, also returns unassigned transmitter units (TX-* serials),
// needed for Digital Twin 3D assignment. Machine builder keeps the default (false) so the
// picker stays focused on assignable channel sensors / motes.
export const getUnassociatedSensorsList = (options = {}) => (dispatch) => {
  const { includeTransmitters = false } = options;
  const request = () => ({ type: sensorsConstants.GET_UNASSOCIATED_SENSORS_LIST_REQUEST });
  const success = sensors =>
    ({ type: sensorsConstants.GET_UNASSOCIATED_SENSORS_LIST_SUCCESS, sensors });
  const failure = error => ({ type: sensorsConstants.GET_UNASSOCIATED_SENSORS_LIST_FAILURE, error });

  const filters = [{ name: 'machine_id', op: 'eq', value: null }];
  if (!includeTransmitters) {
    filters.push({ name: 'type', op: 'ne', value: 'Transmitter' });
  }

  const params = {
    per_page: 500,
    page: 1,
    filters,
  };
  dispatch(request());
  return handleResponse(axiosInstance.get(ENDPOINT.GET_SENSORS_LIST, { params })).then(
    (res) => {
      dispatch(success(res.items));
    },
    (error) => {
      dispatch(failure(error.message));
    }
  );
};

export const setSensorsSorter = sorter => (dispatch) => {
  dispatch({ type: sensorsConstants.SET_SENSORS_SORTER, sorter });
  dispatch({ type: sensorsConstants.CLEAR_SENSORS_LIST });
  dispatch(getSensorsList(null, null, 1));
};

export const setSensorsFilters = (filters, search_key) => (dispatch) => {
  dispatch({ type: sensorsConstants.SET_SENSORS_FILTERS, filters, search_key });
  dispatch({ type: sensorsConstants.CLEAR_SENSORS_LIST });
  dispatch({ type: sensorsConstants.SET_SENSOR_DETAILS_TO_INITIAL_STATE });
  return dispatch(getSensorsList(filters, search_key, 1));
};

// Removes the serial_number from details state.
export const clearDetails = (model, serial_number) => dispatch =>
  dispatch({ type: sensorsConstants.CLEAR_SENSOR_DETAILS, model, serial_number });

export const loadMoreSensors = () => (dispatch, getState) => {
  const sensorsStateItems = getState().sensors.items;
  const page = sensorsStateItems.page;
  if (sensorsStateItems.listLoading) return;
  dispatch({ type: sensorsConstants.INCREASE_PAGE, page: page + 1 });
  dispatch(getSensorsList(null, null, page + 1));
};

export const selectSensor = id =>
  dispatch => dispatch({ type: sensorsConstants.SELECT_SENSOR, id });

// FIXME: Improve the structure in future
export const selectGivenSensor = id =>
  dispatch => dispatch({ type: sensorsConstants.SELECT_GIVEN_SENSOR, id });

export const getSensorDetails = (serialNumber, select = false) => (dispatch, getState) => {
  const request = () => ({ type: sensorsConstants.GET_SENSORS_DETAILS_REQUEST });
  const success = details => ({ type: sensorsConstants.GET_SENSORS_DETAILS_SUCCESS, details });
  const failure = (error, serial_number) => ({ type: sensorsConstants.GET_SENSORS_DETAILS_FAILURE, error, serial_number });

  const addSensorToList = details => ({ type: sensorsConstants.ADD_SENSOR_TO_LIST, details });

  dispatch(request());
  return handleResponse(axiosInstance.get(ENDPOINT.GET_SENSOR_DETAILS(serialNumber))).then(
    (res) => {
      const { items } = getState().sensors;
      if (res.type.includes('Vibration Mote')) {
        const { metadata } = getState().sensors.metadata;
        if (!metadata || !metadata[res.model]) {
          dispatch(failure('Metadata not available.'));
          return null;
        }
        // How many measurement-setting blocks the API returns depends on the
        // device model — temperature-only and some VSX variants ship fewer than
        // the three assumed here. `delete undefined.reporting` throws, so an
        // unguarded index used to take the whole page down with an unhandled
        // rejection as soon as such a device was selected from the list.
        const measurementSettings = res.measurement_settings || {};
        vibrationMeasurementBlocks.forEach(({ index }) => {
          if (measurementSettings[index]) {
            delete measurementSettings[index].reporting;
          }
        });
        const overallSettings = measurementSettings[vibrationMeasurementTypeEnum.overall];
        if (overallSettings) {
          delete overallSettings.sampling;
        }
        const details = {
          serial_number: res.serial_number,
          type: res.type,
          agent_url: res.agent_url,
          device_id: res.device_id,
          mac_address: res.mac_address,
          fs_range: res.fs_range,
          magnetometer_fs_range: res.magnetometer_fs_range,
          last_battery_change_time: res.last_battery_change_time,
          id: res.id,
          last_measurement: res.last_measurement,
          last_report: res.last_report,
          machine: res.machine_id,
          machine_name: res.machine,
          component_name: res.component,
          location_name: res.location,
          component: res.component_id,
          model: res.model,
          location: res.location_id,
          orientation: res.orientation,
          phase: res.phase,
          battery_level: res.battery_and_wireless_info.battery_level,
          wifi_signal: res.battery_and_wireless_info.wifi_signal,
          wifi_rssi: res.battery_and_wireless_info.wifi_rssi,
          wifi_ssid: res.battery_and_wireless_info.wifi_ssid,
          speed_detection: res.speed_detection,
          utilization_detection: res.utilization_detection,
          // Guarded for the same reason as reporting_start_time just below —
          // `reporting` is not guaranteed to be on the payload.
          reporting_interval: res.reporting && res.reporting.interval,
          // Cloud-computed reporting start time ("D-HH:MM", GMT); may be absent
          reporting_start_time: res.reporting && res.reporting.start_time,
          // 0 overall — absent on models that report fewer blocks
          overall: measurementSettings['0'],
          // 1 Full bandwidth
          full_bandwidth: measurementSettings['1'],
          // 2 High res
          high_res: measurementSettings['2'],
          aggressive_measurement: res.aggressive_measurement,
          firmware_versions: res.firmware_versions,
          bluetooth_p2p_enabled: res.bluetooth_p2p_enabled,
          master_device: res.master_device,
          temperature_only: res.temperature_only,
          wifi_backoff: res.wifi_backoff,
          // Boolean debug flag for the detail view; the API returns an object
          // ({ enabled, ... }) so read .enabled explicitly (no optional chaining).
          debug_mode: Boolean(res.debug_mode && res.debug_mode.enabled),
          state_based_measurement: res.state_based_measurement || false,
          config_wifi_ssid: res.wifi_config && res.wifi_config.ssid,
          config_wifi_passphrase: res.wifi_config && res.wifi_config.passphrase,
          config_wifi_start_time: res.wifi_config && res.wifi_config.start_time,
          ap_bssid: res.wifi_ap_config && res.wifi_ap_config.bssid,
          ap_channel: res.wifi_ap_config && res.wifi_ap_config.channel,
          event_detection: res.event_detection ? {
            ...res.event_detection,
            event_configs: toUIFormat(
              (res.event_detection.event_configs && res.event_detection.event_configs.length
                ? res.event_detection.event_configs
                : res.event_detection.device_format) || [],
              getState().sensors.metadata.eventMetadata.data
            )
          } : null
        };

        if (!items.object.find(i => i.serial_number === res.serial_number)) {
          const details = {
            id: 'someid',
            area: res.area,
            last_reported_at: res.last_measurement,
            battery_level: res.battery_and_wireless_info.battery_level,
            serial_number: res.serial_number,
            component: res.component,
            wifi_signal: res.battery_and_wireless_info.wifi_signal,
            type: res.type,
            model: res.model,
            location: res.location,
            machine: res.machine,
            aggressive_measurement: res.aggressive_measurement
          };
          dispatch(addSensorToList(details));
        }
        dispatch(success(details));
      } else if (VectorSensors.includes(res.type)) {
        const details = {
          serial_number: res.serial_number,
          type: res.type,
          source: res.source,
          last_measurement: res.last_measurement,
          tx: res.tx_id,
          tx_serial_number: res.transmitter,
          machine: res.machine_id,
          machine_name: res.machine,
          component_name: res.component,
          location_name: res.location,
          component: res.component_id,
          model: res.model,
          location: res.location_id,
          orientation: res.orientation,
          phase: res.phase,
          // 1 Full bandwidth
          full_bandwidth: res.spectrum_measurements[vibrationMeasurementTypeEnum.fullBandwidth],
          // 2 High res
          high_res: res.spectrum_measurements[vibrationMeasurementTypeEnum.highRes],
          measurement_type: res.spectrum_measurements[vibrationMeasurementTypeEnum.fullBandwidth].measurement.type,
          measurement_interval: res.spectrum_measurements[vibrationMeasurementTypeEnum.fullBandwidth].measurement_interval,
          transmitter_version: res.transmitter_version,
          aggressive_measurement: res.aggressive_measurement,
          tx_reporting_interval: res.tx_reporting_interval,
          tx_poe: res.tx_poe,
          utilization_detection: res.utilization_detection,
          state_based_p2p: res.state_based_p2p,
          simultaneous_measurement_enabled: res.simultaneous_measurement_enabled,
          // overall_measurement_type: res.overall_measurements.measurement.type,
          // overall_reporting_interval: res.overall_measurements.reporting.interval,
        };
        if (!items.object.find(i => i.serial_number === res.serial_number)) {
          const details = {
            id: 'someid',
            area: res.area,
            last_reported_at: res.last_measurement,
            battery_level: null,
            serial_number: res.serial_number,
            component: res.component,
            wifi_signal: null,
            type: res.type,
            model: res.model,
            location: res.location,
            machine: res.machine,
            aggressive_measurement: res.aggressive_measurement
          };
          dispatch(addSensorToList(details));
        }
        dispatch(success(details));
      } else if (res.type === 'Transmitter') {
        const details = {
          serial_number: res.serial_number,
          type: res.type,
          id: res.id,
          last_report: res.last_report,
          model: res.model,
          orientation: res.orientation,
          phase: res.phase,
          battery_level: res.battery_and_wireless_info.battery_level,
          wifi_signal: res.battery_and_wireless_info.wifi_signal,
          wifi_rssi: res.battery_and_wireless_info.wifi_rssi,
          wifi_ssid: res.battery_and_wireless_info.wifi_ssid,
          poe: res.battery_and_wireless_info.poe,
          reporting_interval: res.reporting.interval,
          ports: res.ports,
          aggressive_measurement: res.aggressive_measurement,
          firmware_versions: res.firmware_versions,
          agent_url: res.agent_url,
          device_id: res.device_id,
          mac_address: res.mac_address,
          config_wifi_ssid: res.wifi_config && res.wifi_config.ssid,
          config_wifi_passphrase: res.wifi_config && res.wifi_config.passphrase,
          config_wifi_start_time: res.wifi_config && res.wifi_config.start_time,
          ap_bssid: res.wifi_ap_config && res.wifi_ap_config.bssid,
          ap_channel: res.wifi_ap_config && res.wifi_ap_config.channel
        };
        if (!items.object.find(i => i.serial_number === res.serial_number)) {
          const details = {
            id: 'someid',
            area: res.area,
            last_reported_at: res.last_measurement,
            battery_level: res.battery_and_wireless_info.battery_level,
            serial_number: res.serial_number,
            component: res.component,
            wifi_signal: res.battery_and_wireless_info.wifi_signal,
            type: res.type,
            model: res.model,
            location: res.location,
            machine: res.machine,
            aggressive_measurement: res.aggressive_measurement
          };
          dispatch(addSensorToList(details));
        }
        dispatch(success(details));
      } else if (!StandardSensors.includes(res.type)) {
        const details = {
          serial_number: res.serial_number,
          type: res.type,
          source: res.source,
          tx: res.tx_id,
          tx_serial_number: res.transmitter,
          last_measurement: res.last_measurement,
          machine: res.machine_id,
          machine_name: res.machine,
          component_name: res.component,
          location_name: res.location,
          component: res.component_id,
          model: res.model,
          location: res.location_id,
          orientation: res.orientation,
          phase: res.phase,
          overall_measurement_type: res.overall_measurements.measurement.type,
          overall_measurement_schedule: res.overall_measurements.measurement.schedule,
          overall_measurement_stops: res.overall_measurements.measurement.stops,
          overall_measurement_interval: res.overall_measurements.measurement_interval,
          transmitter_version: res.transmitter_version,
          aggressive_measurement: res.aggressive_measurement,
          tx_reporting_interval: res.tx_reporting_interval
        };
        if (!items.object.find(i => i.serial_number === res.serial_number)) {
          const details = {
            id: 'someid',
            area: res.area,
            last_reported_at: res.last_measurement,
            battery_level: null,
            serial_number: res.serial_number,
            component: res.component,
            wifi_signal: null,
            type: res.type,
            model: res.model,
            location: res.location,
            machine: res.machine,
            aggressive_measurement: res.aggressive_measurement
          };
          dispatch(addSensorToList(details));
        }
        dispatch(success(details));
      }
      if (select) dispatch(selectGivenSensor(serialNumber));
      return res;
    },
    (err) => {
      dispatch(failure(err, serialNumber));
    }
  );
};

export const findSensorDetailRowInMap = (detailsMap, serialNumber) => {
  if (!detailsMap || !serialNumber) return null;
  const keys = Object.keys(detailsMap);
  for (let i = 0; i < keys.length; i += 1) {
    const rows = detailsMap[keys[i]];
    const found = (rows || []).find(r => r.serial_number === serialNumber);
    if (found) return found;
  }
  return null;
};

/**
 * Load sensor details for bulk site transfer when list rows lack tx/id.
 * Returns the normalized detail row from Redux after fetch (via getState).
 */
export const fetchSensorDetailRowForTransfer = serialNumber => async (dispatch, getState) => {
  await dispatch(getSensorDetails(serialNumber));
  return findSensorDetailRowInMap(
    getState().sensors.details.details,
    serialNumber
  );
};

export const deleteSensor = res => (dispatch) => {
  dispatch({ type: sensorsConstants.DELETE_SENSOR_REQUEST });
  return handleResponse(axiosInstance.delete(ENDPOINT.SENSORS, { data: res })).then(
    (data) => {
      dispatch(getHierarchy(false));
      dispatch(getSensorsList());
      return data;
    },
    (error) => {
      dispatch({ type: sensorsConstants.DELETE_SENSOR_FAILURE, error });
      throw error.message;
    }
  );
};

export const updateSensorSerialNumber = (serial_number, model, params) => (dispatch) => {
  const request = () => ({ type: sensorsConstants.UPDATE_SERIAL_NUMBER_REQUEST });
  const success = details => ({ type: sensorsConstants.UPDATE_SERIAL_NUMBER_SUCCESS, details });
  const failure = error => ({ type: sensorsConstants.UPDATE_SERIAL_NUMBER_FAILURE, error });

  dispatch(request());
  return handleResponse(axiosInstance.put(ENDPOINT.UPDATE_SENSOR(serial_number), params)).then(
    (res) => {
      dispatch(success({ ...params, serial_number, model }));
      dispatch(selectSensor(params.new_serial_number));
      toastr.success(res.message);
    },
    (error) => {
      toastr.error(error.message);
      dispatch(failure(error));
    }
  );
};

/**
 * Updates event detection configuration from update entry
 * @param {Object} updateEntry - The update entry data
 * @param {Object} existingDetail - The existing sensor detail object
 * @param {Object} eventMetadata - Event metadata from state
 * @returns {Object|null} Updated event_detection object or null if no update needed
 */
const updateEventDetection = (updateEntry, existingDetail, eventMetadata) => {
  if (!('event_configs' in updateEntry) && !('event_detection_enabled' in updateEntry)) {
    return null;
  }

  let uiFormatConfigs;
  if ('event_configs' in updateEntry) {
    // Transform from device format (in request) to UI format
    const deviceFormatConfigs = updateEntry.event_configs || [];
    uiFormatConfigs = toUIFormat(deviceFormatConfigs, eventMetadata);
  } else {
    // Preserve existing configs (already in UI format)
    uiFormatConfigs = existingDetail.event_detection
      ? existingDetail.event_detection.event_configs
      : [];
  }

  let enabledValue = false;
  if ('event_detection_enabled' in updateEntry) {
    enabledValue = updateEntry.event_detection_enabled;
  } else if (existingDetail.event_detection) {
    enabledValue = existingDetail.event_detection.enabled;
  }

  // Spread first: speed_limits and magnetometer_available describe the mote,
  // and the band check goes quiet without them if the refetch fails.
  return {
    ...(existingDetail.event_detection || {}),
    enabled: enabledValue,
    event_configs: uiFormatConfigs
  };
};

export const updateSensorDetails = res => (dispatch, getState) => {
  const request = () => ({ type: sensorsConstants.UPDATE_DETAILS_REQUEST, res });
  const success = details => ({ type: sensorsConstants.UPDATE_DETAILS_SUCCESS, details });
  const failure = error => ({ type: sensorsConstants.UPDATE_DETAILS_FAILURE, error });
  dispatch(request());
  return handleResponse(axiosInstance.put(ENDPOINT.UPDATE_SENSOR_INFO, res)).then(
    (data) => {
      const { details } = _.cloneDeep(getState().sensors.details);
      Object.entries(res).forEach((entry) => {
        if (!details[entry[0]]) return;
        if (!data[entry[0]] || !data[entry[0]].success) return;
        if (entry[1].type.includes('Vibration Mote')) {
          const list = details[entry[0]].map((detail) => {
            if (!entry[1].serial_number.includes(detail.serial_number)) return detail;
            const measurementSettings = entry[1].measurement_settings;
            if (measurementSettings) {
              // Both sides are optional: the request only carries the blocks the
              // device reports, and the stored detail only has the ones the
              // model supports. Indexing either blind throws here — and because
              // this runs after the PUT resolved, the rejection would skip both
              // success and failure, leaving `updating` stuck true forever.
              vibrationMeasurementBlocks.forEach(({ index, field }) => {
                const incoming = measurementSettings[index];
                if (!incoming) return;
                // No schedule means only the sampling rate changed; a schedule
                // means the block is replaced wholesale.
                if (!incoming.schedule && detail[field]) detail[field].sampling = incoming.sampling;
                else detail[field] = incoming;
              });
            }
            if (entry[1].reporting) detail.reporting_interval = entry[1].reporting.interval;
            if (entry[1].orientation) detail.orientation = entry[1].orientation;
            if (entry[1].phase) detail.phase = entry[1].phase;
            if (entry[1].wifi_config && entry[1].wifi_config.ssid) detail.wifi_ssid = entry[1].wifi_config.ssid;
            if (entry[1].wifi_config && entry[1].wifi_config.passphrase) detail.wifi_passphrase = entry[1].wifi_config.passphrase;
            if (entry[1].wifi_config) {
              detail.config_wifi_ssid = entry[1].wifi_config.ssid;
              detail.config_wifi_passphrase = entry[1].wifi_config.passphrase;
              detail.config_wifi_start_time = entry[1].wifi_config.start_time;
            }
            if (entry[1].wifi_ap_config && entry[1].wifi_ap_config.bssid) detail.ap_bssid = entry[1].wifi_ap_config.bssid;
            if (entry[1].wifi_ap_config && entry[1].wifi_ap_config.channel) detail.ap_channel = entry[1].wifi_ap_config.channel;

            if ('machine' in entry[1]) detail.machine = entry[1].machine;
            if ('machine_name' in entry[1]) detail.machine_name = entry[1].machine_name;
            if ('component' in entry[1]) detail.component = entry[1].component;
            if ('component_name' in entry[1]) detail.component_name = entry[1].component_name;
            if ('location_id' in entry[1]) detail.location = entry[1].location_id;
            if ('location_name' in entry[1]) detail.location_name = entry[1].location_name;
            if ('aggressive_measurement' in entry[1]) detail.aggressive_measurement = entry[1].aggressive_measurement;
            if ('temperature_only' in entry[1]) detail.temperature_only = entry[1].temperature_only;
            if ('wifi_backoff' in entry[1]) detail.wifi_backoff = entry[1].wifi_backoff;
            if ('fs_range' in entry[1]) detail.fs_range = entry[1].fs_range;
            if ('magnetometer_fs_range' in entry[1]) detail.magnetometer_fs_range = entry[1].magnetometer_fs_range;
            // Update event_detection if event_configs or event_detection_enabled was sent
            if ('event_configs' in entry[1] || 'event_detection_enabled' in entry[1]) {
              const eventMetadata = getState().sensors.metadata.eventMetadata.data;
              const updatedEventDetection = updateEventDetection(entry[1], detail, eventMetadata);
              if (updatedEventDetection) {
                detail.event_detection = updatedEventDetection;
              }
            }
            const sensorList = getState().sensors.items.object;
            entry[1].serial_number.forEach((serial_number) => {
              if (entry[1].machine) {
                const item = sensorList.find(s => s.serial_number === serial_number);
                item.machine = entry[1].machine_name;
                item.machine_id = entry[1].machine;
                const filterResourceList = getState().sensors.filterResourceList;
                const machine = filterResourceList.machine.find(r => r.id === entry[1].machine);
                if (machine.area_id) {
                  item.area_id = machine.area_id;
                  const area = filterResourceList.area.find(r => r.id === machine.area_id);
                  item.area = area.name;
                }
              } else if (entry[1].machine === null) {
                const item = sensorList.find(s => s.serial_number === serial_number);
                item.machine = '';
                item.machine_id = null;
                item.area = '';
                item.area_id = null;
                item.location = '';
                item.location_id = null;
              }
              if ('aggressive_measurement' in entry[1]) {
                const item = sensorList.find(s => s.serial_number === serial_number);
                item.aggressive_measurement = entry[1].aggressive_measurement;
              }
              if ('temperature_only' in entry[1]) {
                const item = sensorList.find(s => s.serial_number === serial_number);
                item.temperature_only = entry[1].temperature_only;
              }
            });
            return detail;
          });
          details[entry[0]] = list;
        }
        if (VectorSensors.includes(entry[1].type)) {
          const list = details[entry[0]].map((detail) => {
            if (!entry[1].serial_number.includes(detail.serial_number)) return detail;
            if (entry[1].spectrum_measurements) {
              if (entry[1].spectrum_measurements[vibrationMeasurementTypeEnum.fullBandwidth]) {
                detail.full_bandwidth = entry[1].spectrum_measurements[vibrationMeasurementTypeEnum.fullBandwidth];
                if (entry[1].spectrum_measurements[vibrationMeasurementTypeEnum.fullBandwidth].measurement_interval) {
                  detail.measurement_interval = entry[1].spectrum_measurements[vibrationMeasurementTypeEnum.fullBandwidth].measurement_interval;
                }
                if (entry[1].spectrum_measurements[vibrationMeasurementTypeEnum.fullBandwidth].measurement.type) {
                  detail.measurement_type = entry[1].spectrum_measurements[vibrationMeasurementTypeEnum.fullBandwidth].measurement.type;
                }
              }
              if (entry[1].spectrum_measurements[vibrationMeasurementTypeEnum.highRes]) {
                detail.high_res = entry[1].spectrum_measurements[vibrationMeasurementTypeEnum.highRes];
              }
            }
            if (entry[1].orientation) detail.orientation = entry[1].orientation;
            if (entry[1].phase) detail.phase = entry[1].phase;
            if ('machine' in entry[1]) detail.machine = entry[1].machine;
            if ('machine_name' in entry[1]) detail.machine_name = entry[1].machine_name;
            if ('component' in entry[1]) detail.component = entry[1].component;
            if ('component_name' in entry[1]) detail.component_name = entry[1].component_name;
            if ('location_id' in entry[1]) detail.location = entry[1].location_id;
            if ('location_name' in entry[1]) detail.location_name = entry[1].location_name;
            if ('aggressive_measurement' in entry[1]) detail.aggressive_measurement = entry[1].aggressive_measurement;
            const sensorList = getState().sensors.items.object;
            entry[1].serial_number.forEach((serial_number) => {
              if (entry[1].machine) {
                const item = sensorList.find(s => s.serial_number === serial_number);
                item.machine = entry[1].machine_name;
                item.machine_id = entry[1].machine;
                const filterResourceList = getState().sensors.filterResourceList;
                const machine = filterResourceList.machine.find(r => r.id === entry[1].machine);
                if (machine.area_id) {
                  item.area_id = machine.area_id;
                  const area = filterResourceList.area.find(r => r.id === machine.area_id);
                  item.area = area.name;
                }
              } else if (entry[1].machine === null) {
                const item = sensorList.find(s => s.serial_number === serial_number);
                item.machine = '';
                item.machine_id = null;
                item.area = '';
                item.area_id = null;
                item.location = '';
                item.location_id = null;
              }
              if ('aggressive_measurement' in entry[1]) {
                const item = sensorList.find(s => s.serial_number === serial_number);
                item.aggressive_measurement = entry[1].aggressive_measurement;
              }
            });
            return detail;
          });
          details[entry[0]] = list;
        }
        if (entry[1].type === 'Transmitter') {
          const list = details[entry[0]].map((detail) => {
            if (!entry[1].serial_number.includes(detail.serial_number)) return detail;
            if (entry[1].reporting) detail.reporting_interval = entry[1].reporting.interval;
            if (entry[1].wifi_config && entry[1].wifi_config.ssid) detail.wifi_ssid = entry[1].wifi_config.ssid;
            if (entry[1].wifi_config && entry[1].wifi_config.passphrase) detail.wifi_passphrase = entry[1].wifi_config.passphrase;
            if (entry[1].wifi_config) {
              detail.config_wifi_ssid = entry[1].wifi_config.ssid;
              detail.config_wifi_passphrase = entry[1].wifi_config.passphrase;
              detail.config_wifi_start_time = entry[1].wifi_config.start_time;
            }
            if (entry[1].wifi_ap_config && entry[1].wifi_ap_config.bssid) detail.ap_bssid = entry[1].wifi_ap_config.bssid;
            if (entry[1].wifi_ap_config && entry[1].wifi_ap_config.channel) detail.ap_channel = entry[1].wifi_ap_config.channel;
            if ('aggressive_measurement' in entry[1]) {
              detail.aggressive_measurement = entry[1].aggressive_measurement;
            }
            const sensorList = getState().sensors.items.object;
            entry[1].serial_number.forEach((serial_number) => {
              if ('aggressive_measurement' in entry[1]) {
                const item = sensorList.find(s => s.serial_number === serial_number);
                item.aggressive_measurement = entry[1].aggressive_measurement;
              }
            });
            return detail;
          });
          details[entry[0]] = list;
        }
        if (![...StandardSensors, ...VectorSensors].includes(entry[1].type)) {
          const list = details[entry[0]].map((detail) => {
            if (!entry[1].serial_number.includes(detail.serial_number)) return detail;
            if (entry[1].overall_measurements && entry[1].overall_measurements.measurement) {
              detail.overall_measurement_type = entry[1].overall_measurements.measurement.type;
              detail.overall_measurement_schedule = entry[1].overall_measurements.measurement.schedule;
              detail.overall_measurement_stops = entry[1].overall_measurements.measurement.stops;
            }
            if (entry[1].overall_measurements && entry[1].overall_measurements.measurement_interval) {
              detail.overall_measurement_interval = entry[1].overall_measurements.measurement_interval;
            }

            if ('machine' in entry[1]) detail.machine = entry[1].machine;
            if ('machine_name' in entry[1]) detail.machine_name = entry[1].machine_name;
            if ('component' in entry[1]) detail.component = entry[1].component;
            if ('component_name' in entry[1]) detail.component_name = entry[1].component_name;
            if ('location_id' in entry[1]) detail.location = entry[1].location_id;
            if ('location_name' in entry[1]) detail.location_name = entry[1].location_name;
            const sensorList = getState().sensors.items.object;

            entry[1].serial_number.forEach((serial_number) => {
              if (entry[1].machine) {
                const item = sensorList.find(s => s.serial_number === serial_number);
                item.machine = entry[1].machine_name;
                item.machine_id = entry[1].machine;
                const filterResourceList = getState().sensors.filterResourceList;
                const machine = filterResourceList.machine.find(r => r.id === entry[1].machine);
                if (machine.area_id) {
                  item.area_id = machine.area_id;
                  const area = filterResourceList.area.find(r => r.id === machine.area_id);
                  item.area = area.name;
                }
              } else if (entry[1].machine === null) {
                const item = sensorList.find(s => s.serial_number === serial_number);
                item.machine = '';
                item.machine_id = null;
                item.area = '';
                item.area_id = null;
              }
            });
            return detail;
          });
          details[entry[0]] = list;
        }
      });
      dispatch(success(details));
      dispatch(getHierarchy(false));
      // Returned for callers that need the merged details map without a second GET.
      return details;
    },
    (err) => {
      dispatch(failure(err));
      // Check for nested error structure (e.g. VM3.message)
      if (err.mote_exists) throw err;
      else if (err && typeof err === 'object') {
        // Look for model fields with error messages
        const modelKeys = Object.keys(err);
        for (let i = 0; i < modelKeys.length; i++) {
          const key = modelKeys[i];
          if (err[key] && err[key].success === false && err[key].message) {
            throw err[key];
          }
        }
        throw err;
      } else throw err;
    }
  );
};

export const assignDevicesToAccount = params =>
  () => handleResponse(axiosInstance.put(ENDPOINT.UPDATE_SENSOR_INFO, params)).then(
    (res) => {
      toastr.success(res.message);
      return res;
    },
    (error) => {
      toastr.error(error.message);
      throw error.message;
    }
  );

export const getOrganisationSites = (orgId, { forceRefresh = false } = {}) => (dispatch, getState) => {
  if (!orgId) return Promise.resolve([]);

  const cache = getState().sensors.organisationSitesCache || {};
  const cached = cache[orgId];
  if (!forceRefresh && cached && Array.isArray(cached.items)) {
    return Promise.resolve(cached.items);
  }

  if (!forceRefresh && organisationSitesInFlight[orgId]) {
    return organisationSitesInFlight[orgId];
  }

  organisationSitesInFlight[orgId] = handleResponse(
    axiosInstance.get(ENDPOINT.GET_ORGANISATION_ACCOUNTS(orgId))
  ).then(
    (res) => {
      const items = (res && res.items) || [];
      dispatch({
        type: sensorsConstants.SET_ORGANISATION_SITES_CACHE,
        orgId,
        items
      });
      return items;
    },
    (error) => {
      toastr.error(error.message);
      throw error.message;
    }
  ).finally(() => {
    delete organisationSitesInFlight[orgId];
  });

  return organisationSitesInFlight[orgId];
};

export const transferDeviceToSite = ({
  orgId,
  device_type,
  device_id,
  target_account_id,
  silent = false
}) =>
  () =>
    handleResponse(
      axiosInstance.put(
        ENDPOINT.TRANSFER_DEVICE_BETWEEN_ACCOUNTS(orgId),
        { device_type, device_id, target_account_id }
      )
    ).then(
      res => res,
      (error) => {
        const msg = (error && error.message) || 'Failed to transfer device';
        if (!silent) {
          toastr.error(msg);
        }
        throw msg;
      }
    );

export const getComponentOptions = machine_id => (dispatch, getState) => {
  const componentList = getState().sensors.componentList;
  if (componentList[machine_id] !== undefined) {
    const list = componentList[machine_id].map(res => ({ text: res.name, value: res.id }));
    return Promise.resolve(list);
  }
  return handleResponse(axiosInstance.get(ENDPOINT.GET_MACHINE_COMPONENTS(machine_id)))
    .then((res) => {
      dispatch({ type: sensorsConstants.GET_COMPONENT_LIST_SUCCESS, key: machine_id, value: res.items });
      const optionsList = res.items.map(res => ({ text: res.name, value: res.id }));
      return optionsList;
    }, (error) => {
      throw error.message;
    });
};

export const getLocationOptions = (machine_id, component_id, filter_bearing = false) =>
  (dispatch, getState) => {
    const locationList = getState().sensors.locationList;
    if (locationList[`${machine_id},${component_id}`] !== undefined) {
      const list = locationList[`${machine_id},${component_id}`]
        .filter(r => filter_bearing ? r.bearing : true)
        .map(r => ({ text: r.bearing ? humanize(r.description || r.name) : humanize(r.name), value: r.id, component_id: r.component_id }));
      return Promise.resolve(list);
    }
    return handleResponse(axiosInstance.get(
      ENDPOINT.GET_MACHINE_COMPONENTS_LOCATIONS(machine_id), { params: { component_id } })).then(
      (res) => {
        dispatch({ type: sensorsConstants.GET_LOCATION_LIST_SUCCESS, key: `${machine_id},${component_id}`, value: res });
        return res
          .filter(r => filter_bearing ? r.bearing : true)
          .map(r => ({ text: r.bearing ? humanize(r.description || r.name) : humanize(r.name), value: r.id, component_id: r.component_id }));
      },
      (err) => {
        throw err.message;
      }
    );
  };

export const getSensorsMetadata = () => dispatch =>
  handleResponse(axiosInstance.get(ENDPOINT.GET_SENSORS_METADATA)).then(
    (res) => {
      dispatch({ type: sensorsConstants.GET_SENSORS_METADATA_SUCCESS, data: res });
      return res;
    },
    (error) => {
      dispatch({ type: sensorsConstants.GET_SENSORS_METADATA_FAILURE, error });
      throw error;
    }
  );

export const clearSelectedSensor = () =>
  dispatch => dispatch({ type: sensorsConstants.CLEAR_SELECTED_SENSOR });

export const getResourcesBasedOnType = resource_type =>
  (dispatch) => {
    const params = {
      resource_type
    };
    return handleResponse(axiosInstance(ENDPOINT.GET_FILTER_RESOURCES, { params })).then(
      (res) => {
        dispatch({ type: sensorsConstants.GET_FILTER_RESOURCE_SUCCESS, resource_type, result: res.result });
        if (resource_type === 'component') return res.result.map(r => ({ value: r.id, text: r.name }));
        if (resource_type === 'mcu_firmware') return res.result.map(r => ({ value: r.version, text: r.version }));
        if (resource_type === 'area') return res.result.map(r => ({ value: r.id, text: r.path }));
        return res.result.map(r => ({ value: r.id, text: r.name }));
      },
      (error) => {
        toastr.error(error.message);
      }
    );
  };

export const getTxModels = () =>
  () => handleResponse(axiosInstance.get(ENDPOINT.GET_TX_MODELS)).then(
    res =>
      res,
    (error) => {
      throw error.message;
    }
  );

export const getMoteModels = () =>
  () => handleResponse(axiosInstance.get(ENDPOINT.GET_MOTE_MODELS)).then(
    res =>
      res,
    (error) => {
      throw error.message;
    }
  );

export const createMote = params =>
  () => handleResponse(axiosInstance.post(ENDPOINT.CREATE_MOTE, params)).then(
    res =>
      res,
    (error) => {
      throw error.message;
    }
  );

export const createTx = params =>
  () => handleResponse(axiosInstance.post(ENDPOINT.CREATE_TX, params)).then(
    res =>
      res,
    (error) => {
      throw error.message;
    }
  );

export const createSensor = sensor => (dispatch) => {
  dispatch({ type: sensorsConstants.CREATE_SENSOR_REQUEST });
  return handleResponse(axiosInstance.post(ENDPOINT.SENSORS, sensor)).then(
    (res) => {
      dispatch({ type: sensorsConstants.CREATE_SENSOR_SUCCESS });
      dispatch(getHierarchy(false));
      dispatch(getSensorsList());
      toastr.success(res.message);
      return res;
    },
    (error) => {
      dispatch({ type: sensorsConstants.CREATE_SENSOR_FAILURE, error });
      throw error.message;
    }
  );
};

export const getUnassignedSensors = () => {
  const request = () => ({ type: sensorsConstants.GET_UNASSIGNED_SENSORS_REQUEST });
  const success = sensors => ({ type: sensorsConstants.GET_UNASSIGNED_SENSORS_SUCCESS, sensors });
  const failure = error => ({ type: sensorsConstants.GET_UNASSIGNED_SENSORS_FAILURE, error });

  return (dispatch) => {
    dispatch(request());
    // remove account id
    return handleResponse(axiosInstance.get(ENDPOINT.UNASSIGNED_SENSORS, { params: { unassociated: 1, per_page: 99999999999 } })).then(
      (res) => {
        dispatch(success(res.items));
        return res.items;
      },
      (error) => {
        dispatch(failure(error.message));
        throw error.message;
      }
    );
  };
};

export const assignSensorToTx = (port_id, data, model, serial_number) => dispatch =>
  handleResponse(axiosInstance.post(ENDPOINT.ASSIGN_SENSOR(port_id), data)).then(
    (res) => {
      dispatch(getSensorDetails(serial_number));
      return res;
    },
    (error) => {
      toastr.error(error.message);
      throw error.message;
    }
  );

export const dissociateTxFromMachine = (txId, txSerialNumber) => (dispatch, getState) =>
  handleResponse(axiosInstance.put(ENDPOINT.DISSOCIATE_TX(txId))).then(
    (res) => {
      const sensorsState = getState().sensors;
      // Clear machine association on the TX (and its port sensors) in the details
      // store in place, mirroring updateSensorDetails so the grouped edit view is
      // not wiped by a full list refetch.
      const details = _.cloneDeep(sensorsState.details.details);
      Object.keys(details).forEach((model) => {
        details[model] = details[model].map((d) => {
          // Every sensor on this Tx is unwired by the one call, so any loaded
          // row pointing at it is now port-free. That is the endpoint's
          // contract, not a guess, so patch it rather than refetching each one.
          if (d.serial_number !== txSerialNumber) {
            if (d.tx != null && d.tx === txId) {
              return { ...d, tx: null, tx_id: null };
            }
            return d;
          }
          const updated = {
            ...d,
            machine: null,
            machine_name: null,
            machine_id: null,
            location: null,
            location_name: null,
            location_id: null
          };
          if (Array.isArray(d.ports)) {
            updated.ports = d.ports.map(port => ({
              ...port,
              sensors: (port.sensors || []).map(s => ({
                ...s,
                machine: null,
                machine_id: null,
                machine_name: null,
                location: null,
                location_id: null
              }))
            }));
          }
          return updated;
        });
      });
      dispatch({ type: sensorsConstants.UPDATE_DETAILS_SUCCESS, details });

      // Reflect the change on the list item so reassign eligibility recomputes.
      const item = sensorsState.items.object.find(s => s.serial_number === txSerialNumber);
      if (item) {
        item.machine = '';
        item.machine_id = null;
        item.machines = '';
        item.area = '';
        item.area_id = null;
        // hasDeviceMachineAssociation also reads location/location_id; clear them or reassign stays disabled.
        item.location = '';
        item.location_id = null;
      }

      // Only the Tx needs the server's word: its ports come back empty, which
      // the patch above cannot express (it nulls machine fields but leaves the
      // sensors listed). Same as unassignSensorFromTx.
      dispatch(getSensorDetails(txSerialNumber));

      return res;
    },
    (error) => {
      throw error;
    }
  );

export const unassignSensorFromTx = data => dispatch =>
  handleResponse(axiosInstance.put(ENDPOINT.SENSORS, data)).then(
    (res) => {
      dispatch(getSensorDetails(data.tx_serial_no));
      return res;
    },
    (error) => {
      toastr.error(error.message);
      throw error.message;
    }
  );

export const assignSensorsToTx = sensors => (dispatch) => {
  const request = () => ({ type: sensorsConstants.ASSIGN_SENSORS_TO_TX_REQUEST });
  const success = () => ({ type: sensorsConstants.ASSIGN_SENSORS_TO_TX_SUCCESS });
  const failure = () => ({ type: sensorsConstants.ASSIGN_SENSORS_TO_TX_FAILURE });

  dispatch(request());
  return handleResponse(axiosInstance.post(ENDPOINT.ASSIGN_SENSORS_TO_TX, { sensors })).then(
    (res) => {
      dispatch(success());
      toastr.success(res.message);
    },
    (error) => {
      dispatch(failure());
      toastr.error(error.message);
      throw error.message;
    }
  );
};

export const getDeviceInfoLogs = (serial_number, days, startDate, endDate) => (dispatch) => {
  const request = () => ({ type: sensorsConstants.GET_DEVICE_LOGS_DATA_REQUEST });
  const success = data => ({ type: sensorsConstants.GET_DEVICE_LOGS_DATA_SUCCESS, data });
  const failure = () => ({ type: sensorsConstants.GET_DEVICE_LOGS_DATA_FAILURE });

  const start_date = startDate;
  const end_date = endDate;
  const queryParams = { days, end_date, start_date };

  dispatch(request());
  return handleResponse(
    axiosInstance.get(ENDPOINT.GET_DEVICE_INFO_LOGS(serial_number), { params: queryParams })
  ).then(
    (res) => {
      if (_.isArray(res) && res.length === 0) {
        dispatch(success(res));
      } else {
        dispatch(success(res));
      }
    },
    (error) => {
      dispatch(failure());
      toastr.error(error.message);
    }
  );
};

export const getDeviceLogsData = (mote_id, days, startDate, endDate) => (dispatch) => {
  const request = () => ({ type: sensorsConstants.GET_DEVICE_LOGS_DATA_REQUEST });
  const success = data => ({ type: sensorsConstants.GET_DEVICE_LOGS_DATA_SUCCESS, data });
  const failure = () => ({ type: sensorsConstants.GET_DEVICE_LOGS_DATA_FAILURE });

  const start_date = startDate;
  const end_date = endDate;
  const queryParams = { days, end_date, start_date };

  dispatch(request());
  return handleResponse(
    axiosInstance.get(ENDPOINT.GET_DEVICE_LOGS_DATA(mote_id), { params: queryParams })
  ).then(
    (res) => {
      if (_.isArray(res) && res.length === 0) {
        dispatch(success(res));
      } else {
        dispatch(success(res));
      }
    },
    (error) => {
      dispatch(failure());
      toastr.error(error.message);
    }
  );
};

export const getTxDeviceLogsData = (tx_id, days, startDate, endDate) => (dispatch) => {
  const request = () => ({ type: sensorsConstants.GET_DEVICE_LOGS_DATA_REQUEST });
  const success = data => ({ type: sensorsConstants.GET_DEVICE_LOGS_DATA_SUCCESS, data });
  const failure = () => ({ type: sensorsConstants.GET_DEVICE_LOGS_DATA_FAILURE });

  const start_date = startDate;
  const end_date = endDate;
  const queryParams = { days, end_date, start_date };

  dispatch(request());
  return handleResponse(
    axiosInstance.get(ENDPOINT.GET_TX_DEVICE_LOGS_DATA(tx_id), { params: queryParams })
  ).then(
    (res) => {
      if (_.isArray(res) && res.length === 0) {
        dispatch(success(res));
      } else {
        dispatch(success(res));
      }
    },
    (error) => {
      dispatch(failure());
      toastr.error(error.message);
    }
  );
};

export const getDeviceLastReportedInfo = serialNumber => (dispatch) => {
  const request = () => ({ type: sensorsConstants.GET_DEVICE_LAST_REPORTED_INFO_REQUEST });
  const success = data => ({ type: sensorsConstants.GET_DEVICE_LAST_REPORTED_INFO_SUCCESS, data });
  const failure = () => ({ type: sensorsConstants.GET_DEVICE_LAST_REPORTED_INFO_FAILURE });

  dispatch(request());
  return handleResponse(
    axiosInstance.get(ENDPOINT.GET_DEVICE_LAST_REPORTED_INFO(serialNumber))
  ).then(
    (res) => {
      dispatch(success(res));
    },
    (error) => {
      dispatch(failure());
      toastr.error(error.message);
    }
  );
};

// Export event config actions
export { getEventConfigMetadata } from './eventConfig.actions';
